import React, { useEffect, useMemo, useState } from 'react'
import { API_BASE_URL, createPodcastForResearch } from '../../lib/api'
import { buildReportPdfModel } from '../../lib/pdfExport'
import { downloadReportPdf, resolvePdfImageUrl } from '../../lib/reportPdf'
import { buildCanonicalShareUrl } from '../../lib/shareUrls'
import { resolveAssetUrl } from '../../lib/urlFormatting'
import ShareSheet from '../shared/ShareSheet'
import { ResearchStudy } from '../../pages/ResearchPage'
import { ViewMode } from '../../types'
import { analytics } from '../../lib/analytics'

interface ResearchResultViewerProps {
  study: ResearchStudy
  isOpen: boolean
  onClose: () => void
  viewMode: ViewMode
}

export default function ResearchResultViewer({ study, isOpen, onClose, viewMode }: ResearchResultViewerProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'key-takeaways' | 'full-report' | 'sources'>('overview')
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)
  const researchId = useMemo(() => {
    if (typeof study.researchId === 'number' && study.researchId > 0) {
      return study.researchId
    }

    const parsedId = parseInt(study.id, 10)
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null
  }, [study.id, study.researchId])
  const resolvedCoverImage = useMemo(() => {
    if (!study.image_url) return undefined

    try {
      return resolveAssetUrl(study.image_url, API_BASE_URL)
    } catch {
      return study.image_url
    }
  }, [study.image_url])

  const shareUrl = useMemo(() => {
    return buildCanonicalShareUrl({ researchId })
  }, [researchId])

  const shareText = useMemo(() => {
    return study.results?.summary || 'Check out this research report'
  }, [study.results?.summary])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen || !study.results) return null

  const handleDownload = async () => {
    if (isDownloadingPdf) return

    setIsDownloadingPdf(true)
    try {
      const reportText =
        study.results?.report_text ||
        study.results?.content ||
        study.query ||
        'No content available.'
      const publishedDate = new Date(study.createdAt || Date.now()).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      const pdfCoverImageUrl = resolvedCoverImage && /\/research\/\d+\/image\/?$/.test(resolvedCoverImage)
        ? await resolvePdfImageUrl(resolvedCoverImage)
        : resolvedCoverImage

      await downloadReportPdf(
        buildReportPdfModel({
          title: study.results?.title || study.query || 'Research Report',
          coverImageUrl: pdfCoverImageUrl,
          category: study.category,
          generatedLabel: `Published ${publishedDate}`,
          preparedBy: study.author?.name ? `Prepared by ${study.author.name}` : 'Prepared by Insight Atlas',
          summary: study.results?.summary,
          keyFindings: study.results?.keyFindings || study.results?.key_findings || [],
          reportMarkdown: reportText,
          excludeTocTitles: ['Executive Summary', 'Key Findings', 'References'],
          tocMaxDepth: 2,
          tocMaxEntries: 10,
        }),
      )
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const handleShare = () => {
    if (!shareUrl) {
      alert('Unable to share this article yet.')
      return
    }

    setIsShareSheetOpen(true)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with glass morphism */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal with glass morphism */}
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/50 flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="relative flex items-center justify-between p-8 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-white/60 to-gray-50/60 dark:from-gray-800/60 dark:to-gray-900/60">
          <div className="flex-1 min-w-0 pr-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-tight">
              {study.results.title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 font-medium">
              Research completed on {study.completedAt?.toLocaleDateString()}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {/* TODO: Implement trailer */}}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-xl transition-all duration-200 font-medium"
              title="Listen to trailer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m2 2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v11a2 2 0 002 2z" />
              </svg>
              Trailer
            </button>
            
            {/* Podcast button - TEMPORARILY DISABLED until podcast service is fixed */}
            {/* TODO: Re-enable when podcast generation is stable
            <button
              onClick={async () => {
                try {
                  const rid = parseInt(study.id as unknown as string)
                  const resp = await createPodcastForResearch(rid, { podcast_type: 'short', transcript_only: false })
                  if (resp.audio_url) {
                    const topic = {
                      id: String(study.id),
                      title: study.results?.title || study.title || study.query,
                      description: study.query,
                      summary: study.results?.summary || '',
                      category: study.category || 'General',
                      trending_percentage: 0,
                      engagement_count: 0,
                      sources: study.results?.sources || [],
                      created_at: new Date().toISOString(),
                      tags: []
                    }
                    window.dispatchEvent(new CustomEvent('insightatlas-play-podcast', { detail: { topic, audio_url: resp.audio_url } }))
                  }
                } catch (e) {
                  // no-op for now; parent page likely shows its own toasts
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 rounded-xl transition-all duration-200 font-medium"
              title="Listen to podcast"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              Podcast
            </button>
            */}
            
            <button
              onClick={handleDownload}
              disabled={isDownloadingPdf}
              className={`flex items-center gap-2 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 rounded-xl transition-all duration-200 font-medium ${isDownloadingPdf ? 'opacity-70 cursor-not-allowed' : ''}`}
              title="Download report"
            >
              {isDownloadingPdf ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              {isDownloadingPdf ? 'Generating PDF...' : 'Download'}
            </button>
            
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded-xl transition-all duration-200 font-medium"
              title="Share report"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Share
            </button>

          </div>
          
          {/* Close Button - Separate */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200/50 dark:border-gray-700/50 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm">
          {[
            { key: 'overview', label: 'Overview', icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            )},
            { key: 'key-takeaways', label: 'Key Takeaways', icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )},
            { key: 'full-report', label: 'Full Research Report', icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )},
            { key: 'sources', label: 'Sources', icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-200 ${
                activeTab === tab.key
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-white/60 dark:bg-gray-900/60'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-gray-800/40'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm">
          {activeTab === 'overview' && (
            <div className="p-8 space-y-8">
              {/* Summary */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                  Executive Summary
                </h2>
                <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 p-8 rounded-r-2xl backdrop-blur-sm">
                  <p className="text-gray-800 dark:text-gray-200 leading-relaxed text-lg font-medium">
                    {study.results.summary}
                  </p>
                </div>
              </div>

              {/* Research Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {study.model === 'chatgpt' ? (
                      <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )}
                    <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                      {study.model === 'chatgpt' ? 'GPT-4' : study.model.toUpperCase()}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    AI Model Used
                  </div>
                </div>
                <div className="text-center p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                    {study.results.sources?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Sources Analyzed
                  </div>
                </div>
                <div className="text-center p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                    {Math.ceil((study.results.content?.length || 0) / 1000)}K
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                    Words Generated
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'key-takeaways' && (
            <div className="p-8 space-y-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                Key Takeaways
              </h2>
              
              {/* Key Findings */}
              {study.results.keyFindings && study.results.keyFindings.length > 0 ? (
                <div className="space-y-6">
                  {study.results.keyFindings.map((finding: any, index: number) => (
                    <div key={index} className="flex items-start gap-6 p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 text-white rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 dark:text-gray-200 leading-relaxed font-medium text-lg">
                          {finding}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">
                    No key takeaways extracted from this research
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'full-report' && (
            <div className="p-8 max-w-4xl mx-auto">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-8">
                Full Research Report
              </h2>
              <div className="max-w-none">
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <div 
                    className="leading-relaxed text-gray-800 dark:text-gray-200"
                    style={{
                      fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
                      fontSize: '1.125rem',
                      lineHeight: '1.8'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: study.results.content
                        .replace(/\n\n/g, '</p><p class="mb-6">')
                        .replace(/\n/g, '<br>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
                        .replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold mt-8 mb-4 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">$1</h3>')
                        .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-10 mb-6 text-gray-900 dark:text-white">$1</h2>')
                        .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-12 mb-8 text-gray-900 dark:text-white">$1</h1>')
                        .replace(/^(.+)$/, '<p class="mb-6">$1</p>')
                        // Handle markdown links with only icon
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match: string, text: string, url: string) => {
                          return `<a href="${url}" class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" target="_blank" rel="noopener noreferrer" title="${url}">${text} <svg class="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                        })
                        // Handle bare URLs in parentheses with only icon
                        .replace(/\(https?:\/\/[^\s)]+\)/g, (match: string) => {
                          const url = match.slice(1, -1)
                          return ` <a href="${url}" class="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" target="_blank" rel="noopener noreferrer" title="${url}"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>`
                        })
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sources' && (
            <div className="p-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                Research Sources
              </h2>
              {study.results.sources && study.results.sources.length > 0 ? (
                <div className="space-y-4">
                  {study.results.sources.map((source: any, index: number) => (
                    <div key={index} className="p-6 bg-white/60 dark:bg-gray-800/60 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-800 dark:text-gray-200 leading-relaxed font-medium">
                            {source}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 font-medium">
                    No sources available for this research
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {shareUrl && (
        <ShareSheet
          isOpen={isShareSheetOpen}
          onClose={() => setIsShareSheetOpen(false)}
          title={study.results?.title || study.query || 'Research Report'}
          text={shareText}
          url={shareUrl}
          resourceLabel="article"
          onShareAction={(method) => {
            analytics.trackArticleShared({
              research_id: String(researchId),
              share_method: method,
              is_own_research: true,
            })
          }}
        />
      )}
    </div>
  )
} 